import { useEffect, useState } from "react";

/**
 * Where this app stops calling a screen a phone: Tailwind's `md`.
 *
 * The same line the shell already draws — below it the rail is an off-canvas
 * drawer opened from a top bar, from it up the rail is a static column — so
 * "mobile" means one thing across the app rather than one thing per screen.
 */
export const MOBILE_BREAKPOINT = 768;

// `md:` compiles to `min-width: 768px`, so its complement has to stop just
// short of 768 rather than at 767: a window sitting on a fractional width
// between the two would otherwise match neither and fall through both.
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`;

/**
 * True while the viewport is narrower than `MOBILE_BREAKPOINT`, kept current
 * as the window is resized or the device is turned.
 *
 * For the handful of decisions a stylesheet cannot make — a tab that is not
 * rendered at all rather than hidden, and which one a screen opens on when
 * the usual answer is the one that has gone. Anything that is only a matter
 * of how something looks belongs in a `md:` class instead: this costs a
 * re-render, and a screen that lays itself out in CSS costs nothing.
 *
 * Environments without `matchMedia` — jsdom under the test runner — count as
 * not mobile, so a suite that mounts a screen is not answering a size
 * question only a browser can answer, and sees the whole app.
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? false
      : window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const list = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(list.matches);

    // Re-read on mount as well: the width can have changed between the state
    // initialiser above and this effect.
    update();

    // Safari only gained `addEventListener` on MediaQueryList in 14, and the
    // deprecated `addListener` is all that is there before it.
    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", update);
      return () => list.removeEventListener("change", update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, []);

  return isMobile;
};

export default useIsMobile;
